import { Button, Grid, Theme, Paper, FormControl, InputLabel, MenuItem, Typography, Select as MuiSelect } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/styles';
import { connect } from 'react-redux';
import { IDispatchProp } from 'restdux';
import React from 'react';
import AnonLayout from '../../../components/AnonLayout';
import '../../../styles/App.css';
import { actions as transferWizardActions } from '../../../actions/transferWizard';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { IReturn as ApplicantType, actions as applicantTypeActions } from '../../../api/resources/ApplicantType';
import { actions as locationActions, IReturn as Location } from '../../../api/resources/Location';
import { actions as vendorActions, IReturn as Vendor } from '../../../api/resources/Vendor';
import { ApplicationState } from '../../../reducers';
import { getEnvironmentID, isEnvironmentEmployer } from '../../../selectors/environment';
import { getActiveWizard } from '../../../selectors/wizard';

interface IProps {
    activeWizard?: string;
    applicationTypes: ApplicantType[];
    environmentID?: number;
    isEnvironmentEmployer?: boolean;
    jobLocations: Location[];
    vendors: Vendor[];
}

interface IState {
    applicantType?: number;
    jobLocation?: number;
    vendor?: number;
    notes: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    activeWizard: getActiveWizard(state),
    applicationTypes: Object.values(state.applicanttype.data),
    environmentID: getEnvironmentID(state),
    isEnvironmentEmployer: isEnvironmentEmployer(state),
    jobLocations: Object.values(state.location.data),
    vendors: Object.values(state.vendor.data),
});

const styles = (theme: Theme) =>
    createStyles({
        paper: {
            padding: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(2)}px`,
        },
        form: {
            marginTop: theme.spacing(1),
            width: '100%',
            '&>div': {
                marginTop: theme.spacing(2),
            },
        },
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
            display: 'flex',
            flex: 1,
        },
        centeredButton: {
            marginTop: theme.spacing(2),
        },
    });

type FullProps = WithStyles<typeof styles> & IProps & IDispatchProp & RouteComponentProps;
class SchedulerTransfer extends React.PureComponent<FullProps, IState> {
    state = {
        applicantType: undefined as number | undefined,
        jobLocation: undefined as number | undefined,
        vendor: undefined as number | undefined,
        notes: '' as string,
    };

    public componentDidMount() {
        this.loadDependencies();
    }

    public componentDidUpdate(prevProps: FullProps) {
        const { environmentID } = this.props;

        if (prevProps.environmentID != environmentID)
            this.loadDependencies();
    }

    private async loadDependencies() {
        const { dispatch, environmentID, isEnvironmentEmployer } = this.props;
        if (environmentID) {
            if (isEnvironmentEmployer) {
                await dispatch(applicantTypeActions.index(undefined, undefined, { employer: environmentID, transfer: true } as any)).catch(console.error);
                await dispatch(locationActions.index(undefined, undefined, { employer: environmentID })).catch(console.error);
                await dispatch(vendorActions.index(undefined, undefined, { employer: environmentID })).catch(console.error);
            } else {
                await dispatch(applicantTypeActions.index(undefined, undefined, { network: environmentID, transfer: true } as any)).catch(console.error);
            }
        }
    }

    render() {
        const { classes, applicationTypes, isEnvironmentEmployer, jobLocations, vendors } = this.props;
        const { applicantType, jobLocation, vendor, notes } = this.state;

        const applicantTypeMap = applicationTypes.sort((a, b) => a.description.localeCompare(b.description)).map((entry) => (
            <MenuItem value={entry.id}>{entry.agency && <>{entry.agency} - </>}{entry.description}</MenuItem>
        ));

        const locationMap = jobLocations.sort((a, b) => a.name.localeCompare(b.name)).map((entry) => (
            <MenuItem value={entry.id}>{entry.identifier && `${entry.identifier} - `}{entry.name}</MenuItem>
        ));

        const vendorMap = vendors.sort((a, b) => a.name.localeCompare(b.name)).map((entry) => (
            <MenuItem value={entry.id}>{entry.name}</MenuItem>
        ));

        const aType = applicationTypes.find((entry) => entry.id === applicantType);
        const valid = (applicantType && (!isEnvironmentEmployer || ((!aType?.prompt_location || jobLocation) && (!aType?.prompt_vendor || vendor))));

        return (
            <AnonLayout title='Transfer' subtitle='Select your applicant type and work location'>
                <Paper className={classes.paper}>
                    <form className={classes.form} noValidate autoComplete="off">
                        <FormControl variant="filled" className={classes.formControl}>
                            <InputLabel id="type-label">Select your applicant type</InputLabel>
                            <MuiSelect
                                labelId="type-label"
                                id="select-type"
                                value={applicantType}
                                onChange={(evt) => this.setState({ applicantType: evt.target.value as number })}
                            >
                                {applicantTypeMap}
                            </MuiSelect>
                        </FormControl>
                        {isEnvironmentEmployer && aType?.note && (
                            <Typography variant="h6" className={classes.formControl}>
                                {aType.note}
                            </Typography>
                        )}
                        {isEnvironmentEmployer && aType?.prompt_location && (
                            <FormControl variant="filled" className={classes.formControl}>
                                <InputLabel id="location-label">Select your job/volunteer location</InputLabel>
                                <MuiSelect
                                    labelId="location-label"
                                    id="select-location"
                                    value={jobLocation}
                                    onChange={(evt) => this.setState({ jobLocation: evt.target.value as number })}
                                >
                                    {locationMap}
                                </MuiSelect>
                            </FormControl>
                        )}
                        {isEnvironmentEmployer && aType?.prompt_vendor && (
                            <FormControl variant="filled" className={classes.formControl}>
                                <InputLabel id="vendor-label">Select your vendor</InputLabel>
                                <MuiSelect
                                    labelId="vendor-label"
                                    id="select-vendor"
                                    value={vendor}
                                    onChange={(evt) => this.setState({ vendor: evt.target.value as number })}
                                >
                                    {vendorMap}
                                </MuiSelect>
                            </FormControl>
                        )}
                        <MuiSelect
                            style={{ display: 'none' }}
                            value={notes}
                            onChange={(evt) => this.setState({ notes: (evt.target as any).value })}
                        />
                    </form>
                    <Grid container={true} className={classes.centeredButton} justify="center" spacing={2}>
                        <Button disabled={!valid} onClick={this.onProceed} variant="contained" color="primary">
                            Continue
                        </Button>
                    </Grid>
                </Paper>
            </AnonLayout>
        );
    }

    onProceed = () => {
        const { dispatch, history } = this.props;
        const {
            applicantType,
            jobLocation,
            notes,
            vendor,
        } = this.state;

        if (applicantType === undefined) {
            return;
        }

        dispatch(transferWizardActions.selectType(applicantType, jobLocation, notes, vendor));
        history.push("/data");
    }
}

export default connect(mapStateToProps)(withRouter(withStyles(styles)(SchedulerTransfer)));


